import SiteFooter from '../../components/SiteFooter/SiteFooter';
import SiteHeader from '../../components/SiteHeader/SiteHeader';
import './LegalPage.css';

const pages = {
  privacy: {
    title: 'Privacy Policy',
    updated: 'Last updated: July 6, 2026',
    intro: [
      'Reli ("Reli", "we", "us", or "our") is a mobile application designed to provide short, body-based reset exercises that may help you slow down, refocus, and feel a little lighter.',
      'This Privacy Policy explains what information we collect, how we use it, and the choices you have.',
      'By downloading, accessing, or using Reli, you agree to this Privacy Policy.',
    ],
    sections: [
      {
        title: '1. In Short',
        body: [
          'Reli is designed to be simple and private.',
          'Reli currently collects only limited analytics events about how the app is used. We do not require an account, and we do not collect your name, email address, phone number, contacts, photos, microphone recordings, precise location, or health records.',
          'We use analytics only to understand whether the app is working properly, how people move through the reset flow, and how we can improve the experience.',
        ],
      },
      {
        title: '2. Information We Collect',
        body: [
          'Reli does not currently require users to create an account.',
          'The only information we currently collect is limited app analytics and technical usage data.',
          'This may include information about:',
        ],
        list: [
          'when the app is opened;',
          'which emotion is selected;',
          'when a reset routine starts;',
          'when a routine segment starts or completes;',
          'when a session is paused, resumed, ended, completed, or restarted;',
          'which type of exercise is used;',
          'the number of routine segments;',
          'the duration of a routine;',
          'general app performance and technical diagnostics.',
        ],
        afterList:
          'This information helps us understand how Reli is used, where people may stop, and how we can make the app calmer, simpler, and more helpful.',
      },
      {
        title: '3. Analytics Events We Collect',
        body: [
          'Reli uses analytics to collect limited, event-based information about how the app is used.',
          'These analytics events may include:',
        ],
        list: [
          'app_opened - when the app launches and analytics configuration succeeds;',
          'emotion_selected - when a user taps an emotion;',
          'onboarding_back - when a user taps Back on onboarding;',
          'onboarding_ready - when a user taps the onboarding start or ready button;',
          'routine_intro_shown - when the pre-flow intro screen appears;',
          'routine_started - when a generated routine starts;',
          'segment_started - when each routine segment starts;',
          'segment_completed - when each routine segment completes;',
          'session_paused - when a user taps pause;',
          'session_resumed - when a user continues from the pause popup;',
          'session_ended - when a user ends the session from the pause popup;',
          'routine_completed - when the last segment finishes and the closing screen appears;',
          'closing_done - when a user taps the final closing or done button;',
          'go_again_tapped - when a user taps "Want to go again?";',
          'routine_restarted - when a user confirms or restarts another routine.',
        ],
        bodyAfterFirstList: 'These events may include limited parameters such as:',
        secondList: [
          'selected emotion: Angry, Overwhelmed, Tired, or Stressed;',
          'routine duration in seconds;',
          'number of routine segments;',
          'segment index;',
          'exercise type, such as breathing, moving, hold, tapping, or press;',
          'exercise pattern name, such as a breathing, holding, tapping, moving, or pressing pattern.',
        ],
        afterList: [
          'We use these analytics only to improve Reli, understand which exercises are used, identify where people stop, and make the app more reliable and helpful.',
          'We do not use these analytics to identify you personally.',
        ],
      },
      {
        title: '4. Testing Events',
        body:
          'During development or testing, Reli may include temporary technical events used only to confirm that analytics are working correctly. For example, Reli may include a temporary debug_ping event during testing. This event is intended only for development and should be removed or disabled before public release where possible.',
      },
      {
        title: '5. Information We Do Not Collect',
        body: 'Reli does not intentionally collect:',
        list: [
          'your name;',
          'your email address;',
          'your phone number;',
          'your address;',
          'your contacts;',
          'your photos;',
          'your microphone recordings;',
          'your precise location;',
          'your health records;',
          'written journal entries;',
          'medical diagnosis information;',
          'payment card information directly.',
        ],
        afterList: [
          'Reli also does not currently create user accounts or store a personal profile.',
          'If this changes in a future version of the app, we will update this Privacy Policy and the relevant App Store privacy information.',
        ],
      },
      {
        title: '6. How We Use Information',
        body: 'We use analytics and technical information to:',
        list: [
          'provide and improve Reli;',
          'understand whether users complete a reset;',
          'understand which exercises are used most often;',
          'improve onboarding;',
          'improve the routine flow;',
          'identify bugs or confusing steps;',
          'improve app performance and reliability;',
          'make Reli simpler, calmer, and easier to use.',
        ],
        afterList: 'We do not sell your personal information.',
      },
      {
        title: '7. Third-Party Services',
        body: [
          'Reli may use trusted third-party services to provide analytics, crash reporting, hosting, app functionality, or technical infrastructure.',
          'Currently, Reli may use Firebase Analytics to collect limited app analytics events, such as app opens, emotion selections, routine starts, routine completions, pauses, resumes, and exercise segment activity.',
          'These services may process limited technical or usage data on our behalf. They do not receive your name, email address, contacts, photos, microphone recordings, precise location, or health records from Reli.',
          'We may update the third-party services we use in the future. If this changes in a way that affects what data is collected, we will update this Privacy Policy and the relevant App Store privacy information.',
        ],
      },
      {
        title: '8. Health and Wellness Disclaimer',
        body: [
          'Reli is a wellness and self-care app. It is not a medical device, medical service, therapy, crisis support service, or substitute for professional medical or mental health advice.',
          'Reli does not diagnose, treat, prevent, or cure any medical or mental health condition.',
          'If you are in immediate danger, experiencing a crisis, or feel that you may harm yourself or someone else, contact local emergency services or a qualified crisis support service immediately.',
        ],
      },
      {
        title: '9. Data Retention',
        body: [
          'We keep analytics and technical information only for as long as necessary to provide Reli, improve the app, monitor reliability, comply with legal obligations, resolve disputes, and enforce our agreements.',
          'If Reli stores account data, mood history, or other personal information in a future version, you may request deletion by contacting us at the email below.',
        ],
      },
      {
        title: '10. Your Choices',
        body: 'Depending on your device settings and app version, you may be able to:',
        list: [
          'disable certain analytics or tracking permissions where available;',
          'delete the app from your device;',
          'request deletion of data associated with you, if applicable;',
          'contact us with privacy questions.',
        ],
        afterList: ['To make a privacy request, contact us at:', 'try.reli.app@gmail.com'],
      },
      {
        title: "11. Children's Privacy",
        body: [
          'Reli is not intended for children under the age of 13, or the minimum age required in your country.',
          'We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, contact us and we will take appropriate steps to delete it.',
        ],
      },
      {
        title: '12. International Users',
        body: [
          'If you use Reli from outside the country where we operate, your information may be processed in countries with different data protection laws than your own.',
          'By using Reli, you understand that your information may be processed as described in this Privacy Policy.',
        ],
      },
      {
        title: '13. Changes to This Privacy Policy',
        body: [
          'We may update this Privacy Policy from time to time.',
          'If we make material changes, we will update the "Last updated" date above and may provide additional notice where appropriate.',
          'Your continued use of Reli after changes become effective means you accept the updated Privacy Policy.',
        ],
      },
      {
        title: '14. Contact Us',
        body: ['If you have questions about this Privacy Policy, contact us at:', 'try.reli.app@gmail.com'],
      },
    ],
  },
  terms: {
    title: 'Terms and Conditions',
    updated: 'Last updated: July 6, 2026',
    intro: [
      'These Terms and Conditions ("Terms") describe the rules for using RELI ("RELI", "we", "us", or "our"), including our mobile application, website, and related services.',
      'By downloading, accessing, or using RELI, you agree to these Terms. If you do not agree to these Terms, please do not use RELI.',
    ],
    sections: [
      {
        title: 'Use of RELI',
        body: [
          'RELI provides short, body-based exercises for general wellbeing. You agree to use RELI only for lawful purposes and in a way that does not disrupt, misuse, copy, reverse engineer, or interfere with the app, website, or related services.',
          'RELI is intended for personal, non-commercial use only.',
        ],
      },
      {
        title: 'Not medical advice',
        body: [
          'RELI is a wellness and self-care app. It is not a medical device, medical service, mental-health service, therapy, crisis support service, emergency service, or substitute for care from qualified professionals.',
          'RELI does not diagnose, treat, prevent, or cure any medical or mental health condition.',
          'If you are in immediate danger, experiencing a crisis, or feel that you may harm yourself or someone else, contact local emergency services or a qualified crisis support service immediately.',
        ],
      },
      {
        title: 'No Guarantee of Results',
        body: [
          'RELI is designed to support short moments of reset and reflection. However, we do not guarantee that RELI will reduce stress, improve your mood, calm your nervous system, or create any particular result.',
          'Your experience may vary.',
        ],
      },
      {
        title: 'Your responsibility',
        body: [
          'You are responsible for deciding whether an exercise is appropriate for you.',
          'Stop using RELI immediately if an exercise feels unsafe, painful, uncomfortable, emotionally overwhelming, or unsuitable for you.',
          'You should not use RELI while driving, operating machinery, crossing roads, or doing anything that requires your full attention.',
        ],
      },
      {
        title: 'Device Use and Responsibility',
        body: [
          'Some RELI exercises may ask you to tap, press, hold, or move your fingers on your device screen. You agree to perform all exercises gently and carefully.',
          'Do not press your device with excessive force. Do not use RELI in a way that could damage your device, screen, screen protector, case, accessories, or surrounding objects.',
          'By downloading, accessing, or using RELI, you acknowledge and agree that you are responsible for how you handle and use your own device.',
          'To the fullest extent permitted by law, RELI is not responsible for any damage, loss, or harm caused to your phone, screen, device, accessories, case, screen protector, or other property as a result of your use of the app, including but not limited to pressing too hard, tapping too forcefully, dropping your device, holding your device improperly, or using the app in an unsafe environment.',
          'You agree to use all physical interactions in RELI gently and at your own risk.',
          'Nothing in these Terms excludes or limits liability where it would be unlawful to do so.',
        ],
      },
      {
        title: 'Intellectual property',
        body: [
          'The RELI name, design, logo, content, exercises, interface, graphics, software, and related materials are owned by RELI or its licensors.',
          'You may not copy, modify, reproduce, distribute, sell, sublicense, reverse engineer, or create derivative works based on RELI without our written permission.',
        ],
      },
      {
        title: 'Availability and Changes',
        body: [
          'We may update, change, suspend, or discontinue any part of RELI at any time.',
          'We may add or remove features, exercises, content, or functionality.',
          'We are not responsible if RELI is unavailable at any time or if any feature does not work as expected.',
        ],
      },
      {
        title: 'Privacy',
        body: [
          'Your use of RELI is also governed by our Privacy Policy, which explains how we collect and use information.',
          'You can read our Privacy Policy here: /privacy',
        ],
      },
      {
        title: 'Third-Party Services',
        body: [
          'RELI may use third-party services for analytics, crash reporting, hosting, app functionality, app distribution, or technical infrastructure.',
          'We are not responsible for third-party services, websites, or content that we do not control.',
        ],
      },
      {
        title: 'Limitation of Liability',
        body: [
          'To the fullest extent permitted by law, RELI is provided "as is" and "as available," without warranties of any kind, whether express or implied.',
          'To the fullest extent permitted by law, RELI and its owners, team members, partners, and service providers will not be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, or any loss of data, profits, goodwill, device functionality, or other intangible losses arising from your use of or inability to use RELI.',
          'Nothing in these Terms excludes or limits liability where it would be unlawful to do so.',
        ],
      },
      {
        title: 'Changes to These Terms',
        body: [
          'We may update these Terms from time to time.',
          'If we make changes, we will update the "Last updated" date above. Your continued use of RELI after the updated Terms become effective means you accept the updated Terms.',
        ],
      },
      {
        title: 'Contact',
        body: ['For questions about these Terms, contact the RELI team at:', 'try.reli.app@gmail.com'],
      },
    ],
  },
};

function renderParagraphs(content) {
  const paragraphs = Array.isArray(content) ? content : [content];

  return paragraphs.map((paragraph) => (
    <p key={paragraph}>{paragraph}</p>
  ));
}

function renderList(items) {
  if (!items) {
    return null;
  }

  return (
    <ul>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function LegalPage({ type }) {
  const page = pages[type] || pages.privacy;

  return (
    <main id="top">
      <SiteHeader />
      <section className="legal-page" aria-labelledby="legal-title">
        <a className="legal-back" href="/">Back to home</a>
        <div className="legal-heading">
          <p>{page.updated}</p>
          <h1 id="legal-title">{page.title}</h1>
          {renderParagraphs(page.intro)}
        </div>
        <div className="legal-content">
          {page.sections.map((section) => (
            <section key={section.title}>
              <h2>{section.title}</h2>
              {renderParagraphs(section.body)}
              {renderList(section.list)}
              {section.bodyAfterFirstList && renderParagraphs(section.bodyAfterFirstList)}
              {renderList(section.secondList)}
              {section.afterList && renderParagraphs(section.afterList)}
            </section>
          ))}
        </div>
      </section>
      <SiteFooter />
    </main>
  );
}

export default LegalPage;
